import {
  seededColorFromParams,
  dateToSeed,
  todayDateString,
  yesterdayDateString,
} from '../shared/colorScoring';

export const MAX_LEVEL = 60;

// 60 niveaux progressifs.
// Decouverte 1-10 (60+ pour passer), Precision 11-20 (70+),
// Maitrise 21-30 (80+), Expert 31-60 (85+).
// La severite du scoring augmente aussi avec le niveau (multiplicateur 1.0 -> 3.0
// dans calculateScore) : meme ecart HSL = score plus bas au niveau 60 qu'au niveau 1.
export function getLevelParams(level) {
  if (level <= 3)
    return { sMin: 80, sMax: 95, lMin: 48, lMax: 58, duration: 5000, minScore: 60, tier: 'discovery' };
  if (level <= 6)
    return { sMin: 65, sMax: 85, lMin: 42, lMax: 62, duration: 5000, minScore: 60, tier: 'discovery' };
  if (level <= 10)
    return { sMin: 55, sMax: 80, lMin: 38, lMax: 65, duration: 5000, minScore: 60, tier: 'discovery' };

  if (level <= 13)
    return {
      sMin: 30, sMax: 65, lMin: 35, lMax: 65, duration: 4000, minScore: 70,
      tier: 'precision', specialChance: 0.5, specialType: 'earth',
    };
  if (level <= 16)
    return {
      sMin: 25, sMax: 60, lMin: 30, lMax: 78, duration: 4000, minScore: 70,
      tier: 'precision', specialChance: 0.5, specialType: 'pastel',
    };
  if (level <= 20)
    return {
      sMin: 20, sMax: 55, lMin: 28, lMax: 75, duration: 4000, minScore: 70,
      tier: 'precision', specialChance: 0.5, specialType: 'skin',
    };

  if (level <= 23)
    return {
      sMin: 8, sMax: 40, lMin: 40, lMax: 65, duration: 3000, minScore: 80,
      tier: 'mastery', specialChance: 0.5, specialType: 'grey',
    };
  if (level <= 26)
    return {
      sMin: 12, sMax: 45, lMin: 25, lMax: 48, duration: 3000, minScore: 80,
      tier: 'mastery', specialChance: 0.5, specialType: 'brown',
    };
  if (level <= 30)
    return {
      sMin: 8, sMax: 45, lMin: 25, lMax: 70, duration: 3000, minScore: 80,
      tier: 'mastery',
    };

  // EXPERT 31-60 — couleurs ultra-subtiles, differences quasi invisibles a l'oeil.
  // Observation : 2s. Score requis : 85.00 %+ pour passer.
  if (level <= 35)
    return {
      sMin: 8, sMax: 25, lMin: 30, lMax: 65, duration: 2000, minScore: 85,
      tier: 'expert', specialChance: 0.6, specialType: 'greyCold',
    };
  if (level <= 40)
    return {
      sMin: 5, sMax: 18, lMin: 25, lMax: 55, duration: 2000, minScore: 85,
      tier: 'expert', specialChance: 0.6, specialType: 'brownOlive',
    };
  if (level <= 45)
    return {
      sMin: 3, sMax: 14, lMin: 30, lMax: 60, duration: 2000, minScore: 85,
      tier: 'expert',
    };
  if (level <= 50)
    return {
      sMin: 3, sMax: 12, lMin: 28, lMax: 58, duration: 2000, minScore: 85,
      tier: 'expert', specialChance: 0.5, specialType: 'monochrome',
    };
  if (level <= 55)
    return {
      sMin: 5, sMax: 15, lMin: 72, lMax: 88, duration: 2000, minScore: 85,
      tier: 'expert', specialChance: 0.6, specialType: 'ghostPastel',
    };
  return {
    sMin: 2, sMax: 12, lMin: 25, lMax: 75, duration: 2000, minScore: 85,
    tier: 'expert',
  };
}

const SPECIAL_RANGES = {
  earth: { hMin: 15, hMax: 45, sMin: 30, sMax: 55, lMin: 35, lMax: 55 },
  pastel: { hMin: 0, hMax: 359, sMin: 20, sMax: 40, lMin: 68, lMax: 82 },
  skin: { hMin: 5, hMax: 35, sMin: 25, sMax: 50, lMin: 55, lMax: 75 },
  grey: { hMin: 0, hMax: 359, sMin: 8, sMax: 20, lMin: 40, lMax: 65 },
  brown: { hMin: 15, hMax: 40, sMin: 20, sMax: 45, lMin: 25, lMax: 48 },
  // Expert tier ranges
  greyCold: { hMin: 180, hMax: 270, sMin: 5, sMax: 15, lMin: 35, lMax: 60 },
  brownOlive: { hMin: 30, hMax: 90, sMin: 8, sMax: 20, lMin: 28, lMax: 50 },
  monochrome: { hMin: 0, hMax: 359, sMin: 2, sMax: 10, lMin: 35, lMax: 55 },
  ghostPastel: { hMin: 0, hMax: 359, sMin: 5, sMax: 15, lMin: 72, lMax: 88 },
};

function rand(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function generateTargetColor(level) {
  const params = getLevelParams(level);
  if (params.specialChance && Math.random() < params.specialChance) {
    const r = SPECIAL_RANGES[params.specialType];
    if (r) {
      return { h: rand(r.hMin, r.hMax), s: rand(r.sMin, r.sMax), l: rand(r.lMin, r.lMax) };
    }
  }
  return { h: rand(0, 359), s: rand(params.sMin, params.sMax), l: rand(params.lMin, params.lMax) };
}

export function generateDailyColor(level) {
  const today = todayDateString();
  const seed = dateToSeed(today) + level * 1000003;
  const params = getLevelParams(level);
  return seededColorFromParams(seed, params);
}

export function getTierLabel(level) {
  if (level <= 10) return 'Découverte';
  if (level <= 20) return 'Précision';
  if (level <= 30) return 'Maîtrise';
  return 'Expert';
}

export function getTierEmoji(level) {
  if (level <= 10) return '🟢';
  if (level <= 20) return '🟡';
  if (level <= 30) return '🔴';
  return '⚫';
}

export function isLevelPassed(level, score) {
  return score >= getLevelParams(level).minScore;
}

export function getInitialProgress() {
  return {
    version: 4,
    currentLevel: 1,
    totalGames: 0,
    totalXp: 0,
    bestScorePerLevel: {},
    streak: 0,
    lastPlayedDate: null,
    dailyChallengeLastDate: null,
    dailyChallengeLastScore: null,
    streakBonusesClaimed: [],
  };
}

export function isLegacyProgress(progress) {
  if (!progress || typeof progress !== 'object') return false;
  return progress.version !== 4;
}

const STREAK_BONUSES = { 3: 3, 7: 7, 14: 10, 30: 15 };
const STREAK_MILESTONES = [3, 7, 14, 30];

export function updateProgress(progress, level, score, baseXp, isDaily) {
  const today = todayDateString();
  const yesterday = yesterdayDateString();
  const wasPlayedToday = progress.lastPlayedDate === today;
  const wasPlayedYesterday = progress.lastPlayedDate === yesterday;

  const updated = {
    ...progress,
    bestScorePerLevel: { ...(progress.bestScorePerLevel || {}) },
    streakBonusesClaimed: [...(progress.streakBonusesClaimed || [])],
  };

  updated.totalGames = (updated.totalGames || 0) + 1;

  // Higher score = better
  const prevBest = updated.bestScorePerLevel[level];
  const isNewBest = prevBest === undefined || score > prevBest;
  if (isNewBest) {
    updated.bestScorePerLevel[level] = score;
  }

  let leveledUp = false;
  if (isLevelPassed(level, score) && level === updated.currentLevel && level < MAX_LEVEL) {
    updated.currentLevel = level + 1;
    leveledUp = true;
  }

  if (!wasPlayedToday) {
    updated.streak = wasPlayedYesterday ? (updated.streak || 0) + 1 : 1;
  }
  updated.lastPlayedDate = today;

  const newStreakBonuses = [];
  for (const days of STREAK_MILESTONES) {
    if (updated.streak >= days && !updated.streakBonusesClaimed.includes(days)) {
      newStreakBonuses.push({ days, xp: STREAK_BONUSES[days] });
      updated.streakBonusesClaimed.push(days);
    }
  }
  const streakBonusXp = newStreakBonuses.reduce((sum, b) => sum + b.xp, 0);

  let dailyBonusXp = 0;
  if (isDaily && updated.dailyChallengeLastDate !== today) {
    dailyBonusXp = 5;
    updated.dailyChallengeLastDate = today;
    updated.dailyChallengeLastScore = score;
  }

  const totalXpGained = baseXp + dailyBonusXp + streakBonusXp;
  updated.totalXp = (updated.totalXp || 0) + totalXpGained;

  return {
    progress: updated,
    leveledUp,
    isNewBest,
    newStreakBonuses,
    dailyBonusXp,
    streakBonusXp,
    totalXpGained,
  };
}

export function isDailyChallengePlayed(progress) {
  return progress?.dailyChallengeLastDate === todayDateString();
}
